import React, { useCallback, useMemo } from "react";
import {
  Link,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Business } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CompanyLogo } from "../../../../components/CompanyLogo";
import { LightTooltip } from "../../../../components/LightTooltip";
import { SpogCompany } from "../../../../legacy/types/SpogCompany";
import { CompanyTooltip } from "./CompanyTooltip";
import {
  buildCompanyRouteContextSearch,
  getExploreSearchFromSearch,
  getSegmentIdFromSearch,
} from "@/utils/companyRouteContext";

interface CompanyListItemProps {
  company: SpogCompany;
  isNested?: boolean;
}

export const CompanyListItem: React.FC<CompanyListItemProps> = React.memo(
  ({ company, isNested = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { segmentId: segmentIdFromRoute } = useParams<{
      segmentId?: string;
    }>();
    const segmentContext = useMemo(() => {
      return buildCompanyRouteContextSearch({
        segmentId:
          segmentIdFromRoute ?? getSegmentIdFromSearch(location.search),
        search: getExploreSearchFromSearch(location.search),
      });
    }, [segmentIdFromRoute, location.search]);

    const href = useMemo(
      () => `/catalog/companies/${company.hgId}${segmentContext}`,
      [company.hgId, segmentContext]
    );

    const handleClick = useCallback(
      (event: React.MouseEvent) => {
        // Allow right-click, cmd+click, ctrl+click to open in new tab
        if (event.ctrlKey || event.metaKey || event.button === 2) {
          return; // Let the Link handle it
        }

        // Normal click - use SPA navigation
        event.preventDefault();
        navigate(`/catalog/companies/${company.hgId}${segmentContext}`);
      },
      [navigate, company.hgId, segmentContext]
    );

    const sx = useMemo(
      () => ({
        pl: isNested ? 4 : 2,
        py: isNested ? 0.5 : 1,
        "&:hover": {
          backgroundColor: "action.hover",
          cursor: "pointer",
        },
      }),
      [isNested]
    );

    const listItemIconSx = useMemo(() => ({ minWidth: 36 }), []);

    return (
      <LightTooltip
        title={<CompanyTooltip company={company} />}
        placement="left"
      >
        <ListItem component={Link} href={href} onClick={handleClick} sx={sx}>
          <ListItemIcon sx={listItemIconSx}>
            <CompanyLogo
              domain={company.url}
              size={20}
              variant="rounded"
              fallback={<Business sx={{ fontSize: 16 }} />}
            />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2">{company.name}</Typography>}
          />
        </ListItem>
      </LightTooltip>
    );
  }
);

CompanyListItem.displayName = "CompanyListItem";
